import React, { useState, useEffect, useMemo } from 'react';
import LeftArrowIcon from '../icons/LeftArrowIcon';
import CalendarIcon from '../icons/CalendarIcon';
import Button from './Button';

// px to rem 변환 함수
const pxToRem = (value: number) => `${value / 16}rem`;

// Note 인터페이스
export interface Note {
  id: string;
  senderId: string;
  senderName: string;
  senderProfileImage?: string;
  content: string;
  createdAt: Date;
  isRead: boolean;
}

// NoteDialog props 인터페이스
interface NoteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  notes?: Note[];
  onMarkAsRead?: (noteId: string) => void;
  onMarkAllAsRead?: () => void;
  onNoteClick?: (noteId: string) => void;
  onSendReply?: (noteId: string, content: string, scheduledDate?: Date) => void;
}

const NoteDialog: React.FC<NoteDialogProps> = ({
  isOpen,
  onClose,
  notes = [],
  onMarkAsRead,
  onMarkAllAsRead,
  onNoteClick,
  onSendReply
}) => {
  const [localNotes, setLocalNotes] = useState<Note[]>(notes);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState<string>('');
  const [scheduledDate, setScheduledDate] = useState<Date | null>(null);
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);

  useEffect(() => {
    setLocalNotes(notes);
  }, [notes]);

  // 쪽지 선택 시 상세보기로 전환
  const handleNoteClick = (note: Note) => {
    if (!note.isRead && onMarkAsRead) {
      onMarkAsRead(note.id);
      setLocalNotes(prev =>
        prev.map(n => n.id === note.id ? { ...n, isRead: true } : n)
      );
    }
    setSelectedNoteId(note.id);
    setReplyContent('');
    setScheduledDate(null);
    onNoteClick?.(note.id);
  };

  // 뒤로가기
  const handleBack = () => {
    setSelectedNoteId(null);
    setReplyContent('');
    setScheduledDate(null);
    setShowDatePicker(false);
  };

  // 답장 전송
  const handleSendReply = (isScheduled: boolean = false) => {
    if (!selectedNoteId || !replyContent.trim()) return;
    
    if (isScheduled && scheduledDate) {
      onSendReply?.(selectedNoteId, replyContent, scheduledDate);
    } else if (!isScheduled) {
      onSendReply?.(selectedNoteId, replyContent);
    }
    
    setReplyContent('');
    setScheduledDate(null);
    setShowDatePicker(false);
  };

  const unreadCount = useMemo(() => (
    localNotes.filter(n => !n.isRead).length
  ), [localNotes]);

  // 시간 포맷팅 함수 (오전/오후 형식)
  const formatTime = (date: Date) => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours >= 12 ? '오후' : '오전';
    const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    const displayMinutes = minutes.toString().padStart(2, '0');
    return `${period} ${displayHours}:${displayMinutes}`;
  };

  // 메시지 미리보기 (최대 길이 제한)
  const truncateContent = (content: string, maxLength: number = 40) => {
    if (content.length <= maxLength) return content;
    return content.slice(0, maxLength) + '...';
  };

  // 모든 쪽지 읽음 처리
  const handleMarkAllAsRead = () => {
    setLocalNotes(prev =>
      prev.map(note => ({ ...note, isRead: true }))
    );
    onMarkAllAsRead?.();
  };

  if (!isOpen) return null;

  const selectedNote = selectedNoteId ? localNotes.find(n => n.id === selectedNoteId) : null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        fontFamily: 'Pretendard, sans-serif',
        zIndex: 1000,
        padding: '0 1.25rem'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        style={{
          width: pxToRem(394),
          height: pxToRem(733),
          position: 'relative',
          background: 'var(--Color-background-normal, white)',
          boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.12)',
          borderRadius: pxToRem(16),
          padding: `${pxToRem(24)} ${pxToRem(28)}`,
          display: 'flex',
          flexDirection: 'column',
          gap: pxToRem(40),
          overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {selectedNote ? (
          // 쪽지 상세보기 뷰
          <>
            {/* 상단 섹션 */}
            <div
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: pxToRem(20)
              }}
            >
              {/* 헤더 (뒤로가기 + 발신자 이름) */}
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: pxToRem(11)
                }}
              >
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: pxToRem(8)
                  }}
                >
                  <button
                    type="button"
                    onClick={handleBack}
                    style={{
                      padding: pxToRem(4),
                      borderRadius: pxToRem(8),
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: pxToRem(24),
                      height: pxToRem(24)
                    }}
                  >
                    <LeftArrowIcon width={24} height={24} color="#0D0D0D" />
                  </button>
                  <div
                    style={{
                      flex: 1,
                      textAlign: 'center',
                      color: 'var(--Color-text-normal, #161616)',
                      fontSize: pxToRem(20),
                      fontFamily: 'Pretendard, sans-serif',
                      fontWeight: 600,
                      lineHeight: pxToRem(30),
                      wordWrap: 'break-word'
                    }}
                  >
                    {selectedNote.senderName}
                  </div>
                  <div style={{ width: pxToRem(24) }} /> {/* 공간 맞추기 */}
                </div>
                <div
                  style={{
                    width: '100%',
                    textAlign: 'center',
                    color: 'var(--Color-text-assistive, #989898)',
                    fontSize: pxToRem(16),
                    fontFamily: 'Pretendard, sans-serif',
                    fontWeight: 400,
                    lineHeight: pxToRem(24),
                    wordWrap: 'break-word'
                  }}
                >
                  {formatTime(selectedNote.createdAt)}
                </div>
              </div>

              {/* 원본 쪽지 내용 */}
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <div
                  style={{
                    width: '100%',
                    padding: pxToRem(16),
                    background: 'var(--Color-background-normal, white)',
                    borderRadius: pxToRem(12),
                    border: '1.2px solid var(--Color-line-normal, #F0F0F0)',
                    minHeight: pxToRem(96),
                    color: 'var(--Color-text-normal, #161616)',
                    fontSize: pxToRem(16),
                    fontFamily: 'Pretendard, sans-serif',
                    fontWeight: 500,
                    lineHeight: pxToRem(24),
                    wordWrap: 'break-word',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {selectedNote.content}
                </div>
              </div>
            </div>

            {/* 답장 섹션 */}
            <div
              style={{
                width: '100%',
                padding: pxToRem(16),
                background: 'var(--Color-background-sub, #F8F8F8)',
                borderRadius: pxToRem(8),
                display: 'flex',
                flexDirection: 'column',
                gap: pxToRem(10)
              }}
            >
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: pxToRem(12)
                }}
              >
                <div
                  style={{
                    width: '100%',
                    color: 'var(--Color-text-normal, #161616)',
                    fontSize: pxToRem(16),
                    fontFamily: 'Pretendard, sans-serif',
                    fontWeight: 600,
                    lineHeight: pxToRem(24),
                    wordWrap: 'break-word'
                  }}
                >
                  답장보내기
                </div>
                <div
                  style={{
                    width: '100%',
                    height: pxToRem(84),
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <textarea
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder="내용을 입력해주세요."
                    style={{
                      width: '100%',
                      flex: 1,
                      padding: `${pxToRem(12)} ${pxToRem(16)}`,
                      background: 'var(--Color-fill-white, white)',
                      borderRadius: pxToRem(8),
                      border: '1.2px solid var(--Color-line-normal, #F0F0F0)',
                      color: replyContent ? 'var(--Color-text-normal, #161616)' : 'var(--Color-text-assistive, #989898)',
                      fontSize: pxToRem(13),
                      fontFamily: 'Pretendard, sans-serif',
                      fontWeight: 400,
                      lineHeight: pxToRem(19.5),
                      resize: 'none',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: pxToRem(4),
                  position: 'relative'
                }}
              >
                <div style={{ position: 'relative' }}>
                  <Button
                    variant="submit"
                    width={pxToRem(104)}
                    height={pxToRem(40)}
                    padding={`${pxToRem(8)} ${pxToRem(12)}`}
                    leftIcon={<CalendarIcon width={16} height={16} fill="white" />}
                    onClick={() => {
                      if (scheduledDate) {
                        handleSendReply(true);
                      } else {
                        setShowDatePicker(!showDatePicker);
                      }
                    }}
                    disabled={!replyContent.trim()}
                  >
                    {scheduledDate ? '예약 전송' : '예약 전송'}
                  </Button>
                  {showDatePicker && (
                    <div
                      style={{
                        position: 'absolute',
                        top: pxToRem(-200),
                        left: 0,
                        zIndex: 100,
                        background: 'white',
                        borderRadius: pxToRem(8),
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                        padding: pxToRem(8)
                      }}
                    >
                      <input
                        type="datetime-local"
                        onChange={(e) => {
                          if (e.target.value) {
                            setScheduledDate(new Date(e.target.value));
                            setShowDatePicker(false);
                          }
                        }}
                        style={{
                          border: '1px solid var(--Color-line-normal, #F0F0F0)',
                          borderRadius: pxToRem(4),
                          padding: pxToRem(4),
                          fontSize: pxToRem(14)
                        }}
                      />
                    </div>
                  )}
                </div>
                <Button
                  variant="primary"
                  width={pxToRem(246)}
                  height={pxToRem(40)}
                  padding={`${pxToRem(8)} ${pxToRem(12)}`}
                  onClick={() => handleSendReply(false)}
                  disabled={!replyContent.trim()}
                >
                  바로 전송
                </Button>
              </div>
            </div>
          </>
        ) : (
          // 쪽지 목록 뷰
          <>
        {/* 헤더 */}
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            gap: pxToRem(32)
          }}
        >
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: pxToRem(8),
              position: 'relative'
            }}
          >
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: pxToRem(8),
                minWidth: 0
              }}
            >
              <div
                style={{
                  color: 'var(--Color-text-normal, #161616)',
                  fontSize: pxToRem(20),
                  fontFamily: 'Pretendard, sans-serif',
                  fontWeight: 600,
                  lineHeight: pxToRem(30),
                  wordWrap: 'break-word'
                }}
              >
                쪽지
              </div>
              <div
                style={{
                  color: 'var(--Color-text-assistive, #989898)',
                  fontSize: pxToRem(16),
                  fontFamily: 'Pretendard, sans-serif',
                  fontWeight: 500,
                  lineHeight: pxToRem(24),
                  wordWrap: 'break-word'
                }}
              >
                내 최근 쪽지를 확인하세요.
              </div>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                padding: pxToRem(4),
                borderRadius: pxToRem(8),
                justifyContent: 'center',
                alignItems: 'center',
                display: 'flex',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                width: pxToRem(32),
                height: pxToRem(32),
                flexShrink: 0,
                zIndex: 10,
                position: 'relative'
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  pointerEvents: 'none'
                }}
              >
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="var(--Color-text-strong, #0D0D0D)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          {/* 새로운 쪽지 수 */}
          <div
            style={{
              alignSelf: 'stretch',
              justifyContent: 'flex-start',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '0',
              whiteSpace: 'nowrap'
            }}
          >
            <span style={{ color: 'var(--Color-text-brand-color, #FF6F0F)', fontSize: pxToRem(16), fontFamily: 'Pretendard, sans-serif', fontWeight: 600, lineHeight: pxToRem(24), wordWrap: 'break-word' }}>
              {unreadCount}개
            </span>
            <span style={{ color: 'var(--Color-text-normal, #161616)', fontSize: pxToRem(16), fontFamily: 'Pretendard, sans-serif', fontWeight: 600, lineHeight: pxToRem(24), wordWrap: 'break-word' }}>
              의 새로운 쪽지
            </span>
          </div>

          {/* 쪽지 리스트 */}
          <div
            style={{
              flex: '1 1 auto',
              width: '100%',
              overflowY: 'auto',
              paddingRight: pxToRem(4),
              display: 'flex',
              flexDirection: 'column',
              gap: pxToRem(20),
              minHeight: 0
            }}
          >
            {localNotes.length === 0 ? (
              <div
                style={{
                  width: '100%',
                  padding: `${pxToRem(80)} 0`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: pxToRem(12),
                  color: 'var(--Color-text-assistive, #989898)',
                  fontFamily: 'Pretendard, sans-serif',
                  fontSize: pxToRem(14)
                }}
              >
                <span style={{ fontSize: pxToRem(14) }}>받은 쪽지가 없습니다.</span>
              </div>
            ) : (
              localNotes.map((note, index) => (
                <div
                  key={note.id}
                  style={{
                    position: 'relative',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: pxToRem(8),
                    padding: `${pxToRem(15)} 0`
                  }}
                >
                  <div
                    onClick={() => handleNoteClick(note)}
                    onMouseEnter={() => setHoveredId(note.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      gap: pxToRem(13),
                      cursor: 'pointer'
                    }}
                  >
                    {/* 프로필 이미지 */}
                    <div
                      style={{
                        width: pxToRem(57),
                        height: pxToRem(57),
                        border: '1px solid var(--Color-line-normal, #F0F0F0)',
                        borderRadius: '50%',
                        background: note.senderProfileImage
                          ? `url(${note.senderProfileImage})`
                          : 'var(--Color-background-sub, #F8F8F8)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        flexShrink: 0
                      }}
                    />
                    {/* 쪽지 내용 */}
                    <div
                      style={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: pxToRem(4)
                      }}
                    >
                      {/* 발신자 이름과 시간 */}
                      <div
                        style={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-end',
                          gap: pxToRem(8)
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: pxToRem(9),
                            minWidth: 0,
                            flex: 1
                          }}
                        >
                          <div
                            style={{
                              color: 'var(--Color-text-normal, #161616)',
                              fontSize: pxToRem(16),
                              fontFamily: 'Pretendard, sans-serif',
                              fontWeight: 600,
                              lineHeight: pxToRem(24),
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {note.senderName}
                          </div>
                          {!note.isRead && (
                            <div
                              style={{
                                width: pxToRem(4),
                                height: pxToRem(4),
                                background: 'var(--Color-fill-brand-color, #FF6F0F)',
                                borderRadius: '9999px',
                                flexShrink: 0
                              }}
                            />
                          )}
                        </div>
                        <div
                          style={{
                            color: 'var(--Color-text-assistive, #989898)',
                            fontSize: pxToRem(12),
                            fontFamily: 'Pretendard, sans-serif',
                            fontWeight: 500,
                            lineHeight: pxToRem(18),
                            flexShrink: 0
                          }}
                        >
                          {formatTime(note.createdAt)}
                        </div>
                      </div>
                      {/* 메시지 미리보기 */}
                      <div
                        style={{
                          width: '100%',
                          color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                          fontSize: pxToRem(14),
                          fontFamily: 'Pretendard, sans-serif',
                          fontWeight: 400,
                          lineHeight: pxToRem(21),
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}
                      >
                        {note.content}
                      </div>
                    </div>
                  </div>
                  {/* 구분선 */}
                  {index < localNotes.length - 1 && (
                    <div
                      style={{
                        width: '100%',
                        height: pxToRem(1),
                        background: 'var(--Color-line-normal, #F0F0F0)'
                      }}
                    />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default NoteDialog;

